/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  fetchWithErrorHandling,
  buildGetOptions,
  buildPostOptions,
  buildPutOptions,
  buildDeleteOptions,
} from "../FetchUtils";
import { useAuthUser } from "react-auth-kit";
import { useContext } from "react";
import { AppContext } from "utils/AppContext";
import { getBaseURL } from "utils/Environment";

/**
 * Ruleset API
 */

export const listRulesets = async (accessToken: string, baseURL: string) => {
  return fetchWithErrorHandling(baseURL + "v1/matchmaking/rulesets", buildGetOptions(accessToken));
};

export const createRuleset = async (accessToken: string, baseURL: string, ruleset: any) => {
  return fetchWithErrorHandling(baseURL + "v1/matchmaking/rulesets", buildPostOptions(accessToken, ruleset));
};

export const updateRuleset = async (accessToken: string, baseURL: string, name: string, ruleset: any) => {
  return fetchWithErrorHandling(baseURL + `v1/matchmaking/rulesets/${name}`, buildPutOptions(accessToken, ruleset));
};

export const deleteRuleset = async (accessToken: string, baseURL: string, name: string) => {
  return fetchWithErrorHandling(baseURL + `v1/matchmaking/rulesets/${name}`, buildDeleteOptions(accessToken));
};

/**
 * Queue Configuration API
 */

export const listQueues = async (accessToken: string, baseURL: string) => {
  return fetchWithErrorHandling(baseURL + "v1/matchmaking/queues", buildGetOptions(accessToken));
};

export const createQueue = async (accessToken: string, baseURL: string, queue: any) => {
  return fetchWithErrorHandling(baseURL + "v1/matchmaking/queues", buildPostOptions(accessToken, queue));
};

export const updateQueue = async (accessToken: string, baseURL: string, name: string, queue: any) => {
  return fetchWithErrorHandling(baseURL + `v1/matchmaking/queues/${name}`, buildPutOptions(accessToken, queue));
};

export const deleteQueue = async (accessToken: string, baseURL: string, name: string) => {
  return fetchWithErrorHandling(baseURL + `v1/matchmaking/queues/${name}`, buildDeleteOptions(accessToken));
};

/**
 * Match API
 */

export const listMatches = async (accessToken: string, baseURL: string) => {
  return fetchWithErrorHandling(baseURL + "v1/matchmaking/matches", buildGetOptions(accessToken));
};

/**
 * Ticket API
 */
export const listTickets = async (accessToken: string, baseURL: string) => {
  return fetchWithErrorHandling(baseURL + "v1/matchmaking/tickets", buildGetOptions(accessToken));
};

export const createTicket = async (accessToken: string, baseURL: string, ticket: any) => {
  return fetchWithErrorHandling(baseURL + "v1/matchmaking/tickets", buildPostOptions(accessToken, ticket));
};

export const createBackfillTicket = async (accessToken: string, baseURL: string, ticket: any) => {
  return fetchWithErrorHandling(baseURL + "v1/matchmaking/tickets/backfill", buildPostOptions(accessToken, ticket));
};

/**
 * Metadata API
 */

export const activateQueue = async (
  accessToken: string,
  baseURL: string,
  body: { name: string; queueConfigurationId: string },
) => {
  return fetchWithErrorHandling(baseURL + "v1/matchmaking/metadata/active-queue", buildPostOptions(accessToken, body));
};

export const useMatchmakingService = () => {
  const authUser = useAuthUser();
  const accessToken = authUser()?.access_token;
  const appContext = useContext(AppContext);

  return {
    listRulesets: () => listRulesets(accessToken, getBaseURL(appContext)),
    createRuleset: (ruleset: any) => createRuleset(accessToken, getBaseURL(appContext), ruleset),
    updateRuleset: (name: string, ruleset: any) => updateRuleset(accessToken, getBaseURL(appContext), name, ruleset),
    deleteRuleset: (name: string) => deleteRuleset(accessToken, getBaseURL(appContext), name),

    listQueues: () => listQueues(accessToken, getBaseURL(appContext)),
    createQueue: (queue: any) => createQueue(accessToken, getBaseURL(appContext), queue),
    updateQueue: (name: string, queue: any) => updateQueue(accessToken, getBaseURL(appContext), name, queue),
    deleteQueue: (name: string) => deleteQueue(accessToken, getBaseURL(appContext), name),

    listMatches: () => listMatches(accessToken, getBaseURL(appContext)),

    listTickets: () => listTickets(accessToken, getBaseURL(appContext)),
    createTicket: (ticket: any) => createTicket(accessToken, getBaseURL(appContext), ticket),
    createBackfillTicket: (ticket: any) => createBackfillTicket(accessToken, getBaseURL(appContext), ticket),

    activateQueue: (body: { name: string; queueConfigurationId: string }) =>
      activateQueue(accessToken, getBaseURL(appContext), body),
  };
};
